import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, ReplaySubject } from 'rxjs';

import { NgToJsService } from '@ng2/services';
import { NotificationService, UserService } from '@shared/services';
import { UserStore } from '@signal-store';

import { ContractDetailsDialogData, ContractDetailsResult } from './common';
import type { ContractDetailsComponent } from './contract-details.component';

@Injectable({
  providedIn: 'root',
})
export class ContractDetailsDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly ngToJs = inject(NgToJsService);
  private readonly notification = inject(NotificationService);
  private readonly userService = inject(UserService);
  private readonly userStore = inject(UserStore);

  openContract(data: ContractDetailsDialogData): Observable<ContractDetailsResult> {
    /* Hot: two of the three openers never subscribe, and the dialog still has to open for them. */
    const changed = new ReplaySubject<ContractDetailsResult>(1);
    /** Lazy: the header injects this on every page, and a static import would also cycle back through
     * DmActionsService, which this dialog's own content injects.
     */
    import('./contract-details.component')
      .then(({ ContractDetailsComponent: component }) =>
        this.dialog
          .open<ContractDetailsComponent, ContractDetailsDialogData, ContractDetailsResult>(component, {
            panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full', 'contract-details-dialog'],
            ...this.userService.getResponsiveDialogSize().xl,
            disableClose: true,
            data,
          })
          .afterClosed()
          .subscribe(result => {
            if (result?.contractChanged || result?.contextChanged || result?.periodsChanged) {
              this.applyChanges(result);
              changed.next(result);
            }
            changed.complete();
          }),
      )
      .catch(() => {
        this.notification.error('errors.unidentifiedError');
        changed.complete();
      });
    return changed.asObservable();
  }

  private applyChanges({ contractChanged, periodsChanged }: ContractDetailsResult) {
    if (contractChanged) {
      this.userStore.reloadMenu();
    }
    if (contractChanged || periodsChanged) {
      this.ngToJs.notifyContractModified();
    }
    this.userStore.setReloadContext(true);
  }
}
